import React, { useEffect, useMemo, useState } from 'react';
import {
    Checkbox,
    ChoiceGroup,
    DefaultButton,
    Dropdown,
    IDropdownOption,
    Link,
    MessageBar,
    MessageBarType,
    PanelType,
    Pivot,
    PivotItem,
    PrimaryButton,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    Toggle,
    useTheme,
} from '@fluentui/react';
import {
    useApplyStorefrontTemplateMutation,
    useGetStorefrontTemplateApplyPlanQuery,
} from '../../../store/endpoints/StorefrontTemplateEndpoints';
import { useGetStorefrontSectionsQuery } from '../../../store/endpoints/StorefrontSectionEndpoints';
import {
    IApplyStorefrontTemplateExclusion,
    IApplyStorefrontTemplateSection,
    IApplyStorefrontTemplateSectionStyle,
    IStorefrontTemplateHomeNavigation,
    IStorefrontTemplateSettingChange,
    StorefrontTemplateHomeNavigationChoice,
} from '../../../models/StorefrontTemplateApply';
import { IStorefrontSection } from '../../../models/Storefront';
import { sectionTypeLabel } from '../Editor/Sections/sectionTypes';
import StandardPanel from '../../Common/StandardPanel';
import SectionRealPreview, {
    MIN_PREVIEW_COLUMN,
    sectionWithLook,
    sectionWithoutStyling,
} from './SectionRealPreview';
import { sectionWithStyleRef } from '../Editor/Sections/sectionStyleRef';
import SectionStylePickerModal from './SectionStylePickerModal';
import { StorefrontStylesProvider } from '../Editor/StorefrontStylesContext';
import { IStorefrontStyle } from '../../../models/Storefront';
import { useGetStorefrontStylesQuery } from '../../../store/endpoints/StorefrontStyleEndpoints';
import TemplateChangeRow, { TemplateBlobChanges } from './TemplateChangeRow';
import {
    CHANGE_SECTION_LABELS,
    ChangeSection,
    changeSectionOf,
    changeSize,
    diffConfigBlob,
    isConfigBlobField,
} from './templateChangeDiff';
import { StyleGroupAccordion } from '../Editor/styleControls';

// Applying a template to a storefront that already exists, one decision at a time.
//
// The shape of this screen follows from what the operation actually is. A template's look is what the customer
// asked for, so it is applied; their contact details, social handles and store name are theirs and are never
// touched, so they are not even offered; logos, layout toggles and the global-content flags change more than
// appearance, so each is an explicit opt-in. Sections are the part that cannot be automated: a section is far
// too flexible for one source look to be safely right for all of them, so every one is shown with its before
// and after and included only if the customer says so.
//
// Everything written comes from the plan the API computed and the edits made here. The apply re-derives the
// same plan server-side and writes only what this sends, so nothing can be applied that was not shown.
//
// The result lands on the storefront's DRAFT. That is the whole safety story: the customer previews the draft
// and then publishes or reverts it with the controls they already use, which is also why the feature is only
// available when the organization stages storefront changes through a preview.

type Step = 'look' | 'sections' | 'review';

interface IApplyTemplateWizardProps {
    isOpen: boolean;
    onDismiss: () => void;
    storefrontId: string;
    templateId: string;
    templateName?: string;
    /** Called after a successful apply, so the caller can point the customer at the draft. */
    onApplied?: () => void;
}

const GROUP_LABELS: Record<string, string> = {
    look: 'Theme, colors, fonts and page styling',
    merged: 'Header, home and login styling',
    branding: 'Logo and favicon',
    layoutBehavior: 'Layout and features',
    inheritance: 'Global content',
};

const GROUP_HINTS: Record<string, string> = {
    branding: "Replaces this storefront's logos and favicon with the template's. Off unless you want them.",
    layoutBehavior: 'Page width, banner height, header search, categories and the footer. These change what '
        + 'works on the storefront, not just how it looks.',
    inheritance: "Whether your organization's global sections, banners, links and questions appear on this "
        + 'storefront.',
};


// The brand green the app already tints button icons with (ProductBundleSearchTable and friends spell it as
// a literal). Named once here because three buttons in the same row use it.
const BUTTON_ICON_GREEN = '#789c2f';

const ROLE_LABELS: Record<string, string> = {
    // "Product sections" covers anything that presents products, however they are chosen — a featured row, a
    // saved search, a category/segment row.
    products: 'product sections',
    content: 'content sections',
    unknown: 'other sections',
};

// Field names read better broken at their camelCase humps than as identifiers.
const fieldLabel = (field: string): string =>
    field.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());

const ApplyTemplateWizard: React.FC<IApplyTemplateWizardProps> = (props) => {
    const theme = useTheme();
    const [step, setStep] = useState<Step>('look');
    const [branding, setBranding] = useState(false);
    const [layoutBehavior, setLayoutBehavior] = useState(false);
    const [inheritance, setInheritance] = useState(false);
    const [homeNavigation, setHomeNavigation] = useState<StorefrontTemplateHomeNavigationChoice>('keepCurrent');
    // sectionId -> the look chosen for it. Absent means the section is left alone.
    const [chosen, setChosen] = useState<Record<string, string>>({});
    // Section ids whose own styling the customer has asked to remove. Separate from `chosen` because the two
    // are contradictory answers for one section, and the API refuses a request carrying both.
    const [toClear, setToClear] = useState<Record<string, boolean>>({});
    const [error, setError] = useState<string>();
    // Which change groups are open. Absent means the default for that kind of group, so "expand all" and
    // "collapse all" only have to write the groups that exist.
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
    // Changes struck out with the x on their row, keyed "field" or "field.path" — the same shape the request
    // sends, so what is shown struck through and what is left out cannot drift apart.
    const [excluded, setExcluded] = useState<Record<string, boolean>>({});
    // Two ways to answer step one. "Recommended" takes the template's look and leaves everything the storefront
    // owns alone — which is exactly what the opt-ins default to and what no exclusions means, so it is not a
    // different apply, it is the same apply without being asked about it. "Advanced" is the full surface.
    const [detail, setDetail] = useState<'recommended' | 'advanced'>('recommended');
    // The sections step has the same two ways to answer. Kept separate from step one's: someone may well want
    // the recommended look and hand-picked sections, or the reverse.
    const [sectionDetail, setSectionDetail] = useState<'recommended' | 'advanced'>('recommended');
    // Which section's before/after is on screen. One at a time, chosen from a row of cards: a storefront with
    // a dozen sections is a lot of preview to scroll past, and showing one gives it the full panel width
    // instead of half of a half.
    const [viewedSection, setViewedSection] = useState<string>();
    // The template's own sections the customer wants added to the storefront. Never part of the default plan:
    // adding a section is additive rather than corrective, so it is always something asked for.
    const [toAdd, setToAdd] = useState<Record<string, boolean>>({});
    // Sections pointed at one of the organization's own named section styles, by section id → style id. A
    // third map rather than a field on `chosen`, for the same reason `toClear` is its own: the three are
    // mutually exclusive answers and the API refuses a section that appears in more than one list, so the
    // exclusivity is enforced in one place (setAction) rather than trusted at every call site.
    const [styled, setStyled] = useState<Record<string, string>>({});
    // The section whose style-picker modal is open, if any.
    const [stylePickerFor, setStylePickerFor] = useState<string>();

    const plan = useGetStorefrontTemplateApplyPlanQuery(
        { storefrontId: props.storefrontId, templateId: props.templateId },
        { skip: !props.isOpen });
    // The storefront's own sections, for the "now" side of each before/after. Read separately rather than
    // carried on the plan: they are this organization's own documents and already fetchable, unlike the
    // template's, which for a platform template live in another organization.
    const sections = useGetStorefrontSectionsQuery(props.storefrontId, { skip: !props.isOpen });
    // The storefront's named styles, so a section previewing a `$styleRef` resolves it to the same values the
    // editor would. Without the provider below the refs fall back to the element's own styling and the preview
    // quietly under-sells anything built on a named style.
    const storefrontStyles = useGetStorefrontStylesQuery(props.storefrontId, { skip: !props.isOpen });
    const [applyTemplate, applyState] = useApplyStorefrontTemplateMutation();

    const changesByGroup = useMemo(() => {
        const groups: Record<string, IStorefrontTemplateSettingChange[]> = {};
        for (const change of plan.data?.settingChanges ?? []) {
            (groups[change.group] ??= []).push(change);
        }
        return groups;
    }, [plan.data]);

    const sectionById = useMemo(() => {
        const byId: Record<string, IStorefrontSection> = {};
        for (const section of sections.data?.items ?? []) {
            if (section.id) byId[section.id] = section;
        }
        return byId;
    }, [sections.data]);

    // Sections the customer can actually decide about: the template has at least one look for their kind, and
    // they are not already wearing the proposal.
    const alreadyMatching = (plan.data?.sectionMatches ?? []).filter((m) => m.alreadyMatches).length;

    // True when the template offers no per-section look anywhere: it styles its sections through the theme and
    // the page styling instead, which the previous step already applies. Worth saying out loud, because
    // "nothing to restyle" next to a template that visibly restyles a storefront reads as a broken screen.
    const templateStylesSectionsGlobally = (plan.data?.templateLooks ?? [])
        .every((role) => role.options.length === 0);

    // A plan built with no draft yet carries LIVE section ids, and creating the draft renumbers them. So the
    // section step is only offered once a draft exists; applying the look is what creates one.
    const sectionsAvailable = plan.data?.fromExistingDraft === true;

    const chooseRecommended = () => {
        setDetail('recommended');
        // What is shown has to be what is sent. Leaving advanced answers in place behind a summary that does
        // not mention them is the one thing this mode must not do.
        setBranding(false);
        setLayoutBehavior(false);
        setInheritance(false);
        setExcluded({});
        setHomeNavigation('keepCurrent');
    };

    // Take the plan's proposals as the starting answer. The plan only ever proposes a look where the template
    // has exactly ONE for that kind of section — the ambiguous case deliberately proposes nothing — so a
    // proposal is already the unambiguous answer and making the customer re-assert it is busywork. They can
    // still untick any of them, and the before/after is right there to judge by.
    //
    // Re-seeded whenever a new plan arrives, which is correct: a refetched plan means the proposals changed,
    // and choices made against the old one no longer describe anything.
    useEffect(() => {
        if (!plan.data) return;
        setChosen(proposedChoices());
        setToClear(recommendedClears());
        // Keyed on plan.data alone, deliberately: the seed is derived from it, and re-running whenever those
        // derivations change identity would re-seed on every render and discard the customer's choices.
    }, [plan.data]);

    // Back to the default plan, so the recommended list cannot describe one thing while the apply sends
    // another. Seeded from the plan the same way the initial state is.
    const chooseRecommendedSections = () => {
        setSectionDetail('recommended');
        setChosen(proposedChoices());
        setToClear(recommendedClears());
        // Adds are never part of the default plan, so returning to it means dropping them - otherwise the
        // summary would describe a plan that quietly also creates sections.
        setToAdd({});
    };

    const exclusionKey = (field: string, path?: string) => (path ? `${field}\u0000${path}` : field);
    const isExcluded = (field: string, path?: string) => !!excluded[exclusionKey(field, path)];
    const toggleExcluded = (field: string, path?: string) => setExcluded((e) => {
        const key = exclusionKey(field, path);
        return { ...e, [key]: !e[key] };
    });
    const exclusionFor = (field: string, path?: string) => ({
        excluded: isExcluded(field, path),
        onToggle: () => toggleExcluded(field, path),
    });

    const exclusions: IApplyStorefrontTemplateExclusion[] = Object.entries(excluded)
        .filter(([, on]) => on)
        .map(([key]) => {
            const [field, path] = key.split('\u0000');
            return path ? { field, path } : { field };
        });

    // The organization's own reusable section styling. The wizard already reads these so a section built on a
    // `$styleRef` previews correctly; offering them as a choice is the same list seen from the other end.
    // Empty means the option is never offered — an organization that keeps no section styles should not be
    // shown a control whose modal would open onto nothing.
    const sectionStyles: IStorefrontStyle[] = (storefrontStyles.data?.items ?? [])
        .filter((s) => s.elementType === 'section');
    const sectionStyleById = new Map(sectionStyles.map((s) => [s.id, s]));

    const chosenSections: IApplyStorefrontTemplateSection[] = Object.entries(chosen)
        .filter(([, fingerprint]) => !!fingerprint)
        .map(([sectionId, fingerprint]) => ({ sectionId, fingerprint }));
    const clearedSections = Object.entries(toClear).filter(([, on]) => on).map(([sectionId]) => sectionId);
    const styledSections: IApplyStorefrontTemplateSectionStyle[] = Object.entries(styled)
        .filter(([, styleId]) => !!styleId)
        .map(([sectionId, styleId]) => ({ sectionId, styleId }));
    const addedSections = Object.entries(toAdd).filter(([, on]) => on).map(([sectionId]) => sectionId);
    const addable = plan.data?.addableSections ?? [];


    // Counts are of what will actually be written, so the review step cannot promise a struck-out change.
    const applyingCount = (group?: IStorefrontTemplateSettingChange[]) => (group ?? []).filter((change) => {
        if (isExcluded(change.field)) return false;
        if (!isConfigBlobField(change.field)) return true;
        const properties = diffConfigBlob(change.from, change.to);
        return properties.some((p) => !isExcluded(change.field, p.path));
    }).length;

    const lookChangeCount = changesByGroup.look?.length ?? 0;
    const optedInChangeCount =
        (branding ? changesByGroup.branding?.length ?? 0 : 0)
        + (layoutBehavior ? changesByGroup.layoutBehavior?.length ?? 0 : 0)
        + (inheritance ? changesByGroup.inheritance?.length ?? 0 : 0);
    const mergedChangeCount = changesByGroup.merged?.length ?? 0;
    const nothingToDo = lookChangeCount + optedInChangeCount + mergedChangeCount === 0
        && chosenSections.length === 0
        && clearedSections.length === 0
        && styledSections.length === 0
        && addedSections.length === 0
        && homeNavigation === 'keepCurrent';


    const apply = async () => {
        setError(undefined);
        try {
            const result = await applyTemplate({
                storefrontId: props.storefrontId,
                request: {
                    templateId: props.templateId,
                    branding,
                    layoutBehavior,
                    inheritance,
                    homeNavigation,
                    exclusions,
                    sections: chosenSections,
                    sectionsToClear: clearedSections,
                    sectionsToAdd: addedSections,
                    sectionsToStyle: styledSections,
                },
            }).unwrap();
            if (result.success === false) {
                setError(result.message || 'The template could not be applied.');
                return;
            }
            props.onApplied?.();
            props.onDismiss();
        } catch {
            setError('The template could not be applied. Nothing has been changed.');
        }
    };

    // A template can change forty things at once, and rendering all of them flat is how the step became a
    // wall of text. Each configuration document gets its own collapsible group carrying the count of
    // properties inside it, and the handful of scalars collapse together by what they affect.
    //
    // Scalars start OPEN and documents start CLOSED: the theme, its colors and its fonts are few and are what
    // a person actually recognizes a template by, while the documents are dozens of CSS properties that only
    // matter when someone goes looking.
    const changeGroups = (changes: IStorefrontTemplateSettingChange[]) => {
        const scalars = new Map<ChangeSection, IStorefrontTemplateSettingChange[]>();
        const documents: IStorefrontTemplateSettingChange[] = [];
        for (const change of changes) {
            if (isConfigBlobField(change.field)) {
                documents.push(change);
                continue;
            }
            const section = changeSectionOf(change.field);
            const existing = scalars.get(section);
            if (existing) existing.push(change);
            else scalars.set(section, [change]);
        }
        return { scalars: Array.from(scalars.entries()), documents };
    };

    const groupKeys = (changes: IStorefrontTemplateSettingChange[]): string[] => {
        const { scalars, documents } = changeGroups(changes);
        return [...scalars.map(([section]) => `scalar:${section}`), ...documents.map((d) => `doc:${d.field}`)];
    };

    const isOpen = (key: string, fallback: boolean) => openGroups[key] ?? fallback;
    const setOpen = (key: string, open: boolean) => setOpenGroups((g) => ({ ...g, [key]: open }));

    const renderChangeGroups = (changes: IStorefrontTemplateSettingChange[]) => {
        const { scalars, documents } = changeGroups(changes);
        return (
            <Stack>
                {scalars.map(([section, sectionChanges]) => {
                    const key = `scalar:${section}`;
                    const applying = sectionChanges.filter((c) => !isExcluded(c.field)).length;
                    return (
                        <StyleGroupAccordion
                            key={key}
                            label={CHANGE_SECTION_LABELS[section]}
                            secondaryLabel={applying === sectionChanges.length
                                ? `${sectionChanges.length}`
                                : `${applying} of ${sectionChanges.length}`}
                            expanded={isOpen(key, true)}
                            onToggle={(open) => setOpen(key, open)}
                        >
                            <Stack tokens={{ childrenGap: 4 }} styles={{ root: { paddingLeft: '1.5em' } }}>
                                {sectionChanges.map((change) => (
                                    <TemplateChangeRow
                                        key={change.field}
                                        field={change.field}
                                        label={fieldLabel(change.field)}
                                        from={change.from}
                                        to={change.to}
                                        exclusion={exclusionFor(change.field)}
                                    />
                                ))}
                            </Stack>
                        </StyleGroupAccordion>
                    );
                })}
                {documents.map((change) => {
                    const key = `doc:${change.field}`;
                    const size = changeSize(change.field, change.from, change.to);
                    const left = diffConfigBlob(change.from, change.to)
                        .filter((c) => isExcluded(change.field, c.path)).length;
                    return (
                        <StyleGroupAccordion
                            key={key}
                            label={fieldLabel(change.field)}
                            secondaryLabel={left === 0
                                ? `${size} ${size === 1 ? 'property' : 'properties'}`
                                : `${size - left} of ${size} properties`}
                            expanded={isOpen(key, false)}
                            onToggle={(open) => setOpen(key, open)}
                        >
                            <Stack styles={{ root: { paddingLeft: '1.5em' } }}>
                                {/* No cap here: the group is closed until someone asks for it, and a person
                                    who opened it wants the whole list rather than "and 34 more". */}
                                <TemplateBlobChanges
                                    from={change.from}
                                    to={change.to}
                                    maxRows={Number.MAX_SAFE_INTEGER}
                                    exclusionFor={(path) => exclusionFor(change.field, path)}
                                />
                            </Stack>
                        </StyleGroupAccordion>
                    );
                })}
            </Stack>
        );
    };

    const renderExpandAll = (changes: IStorefrontTemplateSettingChange[]) => {
        const keys = groupKeys(changes);
        if (keys.length < 2) return null;
        const allOpen = keys.every((key) => isOpen(key, key.startsWith('scalar:')));
        return (
            <Link onClick={() => {
                const next: Record<string, boolean> = {};
                for (const key of keys) next[key] = !allOpen;
                setOpenGroups((g) => ({ ...g, ...next }));
            }}>
                {allOpen ? 'Collapse all' : 'Expand all'}
            </Link>
        );
    };

    const renderGroupToggle = (group: 'branding' | 'layoutBehavior' | 'inheritance',
        checked: boolean, onChange: (value: boolean) => void) => {
        const changes = changesByGroup[group] ?? [];
        if (changes.length === 0) return null;
        return (
            <Stack tokens={{ childrenGap: 4 }}>
                <Toggle
                    label={`${GROUP_LABELS[group]} (${changes.length})`}
                    inlineLabel
                    checked={checked}
                    onChange={(_e, value) => onChange(!!value)}
                />
                <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    {GROUP_HINTS[group]}
                </Text>
                {checked && (
                    <Stack tokens={{ childrenGap: 4 }} styles={{ root: { paddingLeft: '1em' } }}>
                        {changes.map((change) => (
                            <TemplateChangeRow
                                key={change.field}
                                field={change.field}
                                label={fieldLabel(change.field)}
                                from={change.from}
                                to={change.to}
                            />
                        ))}
                    </Stack>
                )}
            </Stack>
        );
    };

    // Two rows of navigation tiles on one home page. The category pods come from a storefront setting this
    // apply leaves alone; the nav pods are authored inside the template's home styling. Applying a
    // nav-pod-based design onto a storefront already showing category pods stacked both — so it is asked.
    //
    // Advanced only. The safe answer is to change nothing, which is what Recommended does and says it does;
    // taking the template's tiles means replacing navigation the customer authored, which is not something to
    // decide on their behalf.
    const renderHomeNavigation = (nav: IStorefrontTemplateHomeNavigation) => {
        return (
            <Stack tokens={{ childrenGap: 4 }}>
                <Text variant='mediumPlus'>Home page navigation</Text>
                <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    This storefront shows the automatic category tiles on its home page, and this template
                    brings {nav.templateNavPodCount} shortcut {nav.templateNavPodCount === 1 ? 'tile' : 'tiles'}
                    {' '}of its own. Showing both stacks two rows of tiles, so pick one.
                </Text>
                <ChoiceGroup
                    selectedKey={homeNavigation}
                    onChange={(_e, option) => option
                        && setHomeNavigation(option.key as StorefrontTemplateHomeNavigationChoice)}
                    options={[
                        {
                            key: 'keepCurrent',
                            text: 'Keep the category tiles (nothing changes here)',
                        },
                        {
                            key: 'useTemplateNavPods',
                            text: nav.storefrontNavPodCount > 0
                                ? `Use the template's shortcut tiles instead — replaces your ${nav.storefrontNavPodCount} and turns the category tiles off`
                                : "Use the template's shortcut tiles instead — turns the category tiles off",
                        },
                        { key: 'showBoth', text: 'Show both rows' },
                    ]}
                />
            </Stack>
        );
    };

    // What "recommended" actually does, in the plainest terms available: the groups it changes, and the list of
    // things it will not touch. The second half is the reassurance that makes the first half safe to accept
    // without reading every row.
    const renderRecommendedSummary = () => {
        const { scalars, documents } = changeGroups(changesByGroup.look ?? []);
        const documentProperties = documents
            .reduce((total, change) => total + changeSize(change.field, change.from, change.to), 0);
        const parts = [
            ...scalars.map(([section, changes]) =>
                `${CHANGE_SECTION_LABELS[section].toLowerCase()} (${changes.length})`),
            ...(documents.length > 0
                ? [`${documents.length} ${documents.length === 1 ? 'page' : 'pages'} of styling `
                    + `(${documentProperties} ${documentProperties === 1 ? 'property' : 'properties'})`]
                : []),
        ];

        return (
            <Stack tokens={{ childrenGap: '0.75em' }}>
                {parts.length === 0
                    ? <Text>This storefront already matches the template&apos;s styling.</Text>
                    : (
                        <Stack tokens={{ childrenGap: 4 }}>
                            <Text variant='mediumPlus'>What this changes</Text>
                            <Text variant='small'>{parts.join(', ')}.</Text>
                        </Stack>
                    )}
                {mergedChangeCount > 0 && (
                    <Text variant='small'>
                        Header, home and login styling is applied a property at a time, so your header links,
                        store name, shortcut tiles, partner logos and login wording are kept.
                    </Text>
                )}
                <Stack tokens={{ childrenGap: 4 }}>
                    <Text variant='mediumPlus'>What stays as it is</Text>
                    <Text variant='small'>
                        Your contact details and social links, your store title and banner text, your logos and
                        favicon, your page width and header, category and footer settings, and whether your
                        organization&apos;s global content appears here.
                    </Text>
                    {plan.data?.homeNavigation?.conflict && (
                        // Not a question here — the safe answer is to change nothing — but not invisible
                        // either: this template brings shortcut tiles, and a customer who wanted them should
                        // be able to find out where to say so.
                        <Text variant='small'>
                            Your home page keeps its category tiles. This template also brings
                            {' '}{plan.data.homeNavigation.templateNavPodCount} shortcut
                            {' '}{plan.data.homeNavigation.templateNavPodCount === 1 ? 'tile' : 'tiles'} of its
                            own, which Advanced lets you take instead.
                        </Text>
                    )}
                </Stack>
                <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    Switch to Advanced to see every setting and leave individual ones out.
                </Text>
            </Stack>
        );
    };

    const renderLookStep = () => (
        <Stack tokens={{ childrenGap: '1em' }}>
            <MessageBar messageBarType={MessageBarType.info}>
                Changes are saved to this storefront&apos;s draft. The live storefront stays as it is until you
                publish.
            </MessageBar>

            <Pivot
                selectedKey={detail}
                onLinkClick={(item) => (item?.props.itemKey === 'advanced'
                    ? setDetail('advanced')
                    : chooseRecommended())}
            >
                <PivotItem itemKey='recommended' headerText='Recommended' />
                <PivotItem itemKey='advanced' headerText='Advanced' />
            </Pivot>

            {detail === 'recommended' && renderRecommendedSummary()}
            {detail === 'advanced' && renderAdvancedLook()}
        </Stack>
    );

    const renderAdvancedLook = () => (
        <Stack tokens={{ childrenGap: '1em' }}>
            <Stack tokens={{ childrenGap: 4 }}>
                <Text variant='mediumPlus'>{GROUP_LABELS.look}</Text>
                <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    This is the template&apos;s look, and it is what gets applied. Your contact details, social
                    links, store title and banner text are never changed by a template.
                </Text>
                {lookChangeCount === 0
                    ? <Text variant='small'>This storefront already matches the template&apos;s styling.</Text>
                    : (
                        <>
                            {renderExpandAll(changesByGroup.look)}
                            {renderChangeGroups(changesByGroup.look)}
                        </>
                    )}
            </Stack>

            {mergedChangeCount > 0 && (
                <>
                    <Separator />
                    <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant='mediumPlus'>{GROUP_LABELS.merged}</Text>
                        <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                            Applied a property at a time, so your header links, your store name, your home-page
                            shortcut tiles, your partner logos and your login wording all stay exactly as they
                            are. What is listed below is what would be written.
                        </Text>
                        {renderChangeGroups(changesByGroup.merged)}
                    </Stack>
                </>
            )}

            {plan.data?.homeNavigation?.conflict && (
                <>
                    <Separator />
                    {renderHomeNavigation(plan.data.homeNavigation)}
                </>
            )}

            <Separator />
            {renderGroupToggle('branding', branding, setBranding)}
            {renderGroupToggle('layoutBehavior', layoutBehavior, setLayoutBehavior)}
            {renderGroupToggle('inheritance', inheritance, setInheritance)}
        </Stack>
    );

    // ---- The sections step ----
    //
    // Every section gets ONE action: take one of the template's looks, have its own styling removed, or be left
    // alone. They are mutually exclusive answers to the same question, so they are one control rather than a
    // checkbox plus a dropdown plus another checkbox.
    //
    // Any look may be chosen for any section. The PLAN only proposes within a kind — proposing a product row's
    // styling for a rich-text block would be a guess — but a customer saying "use that one here" is not a
    // guess, and refusing it made the common case unworkable: a template with a single section look had nothing
    // to offer sections of every other kind.

    type SectionAction =
        | { kind: 'leave' }
        | { kind: 'look'; fingerprint: string }
        | { kind: 'clear' }
        | { kind: 'style'; styleId: string };

    const LEAVE_KEY = 'leave';
    const CLEAR_KEY = 'clear';
    const lookKey = (fingerprint: string) => `look:${fingerprint}`;
    // Opens the picker rather than selecting a style: which style is a second question, and a dropdown that
    // silently picked the first one would be answering it for the customer.
    const STYLE_KEY = 'style:pick';
    const styleKey = (styleId: string) => `style:${styleId}`;

    // Every distinct look the template offers, with the kind of section it came from. Styling-free looks are
    // left out: choosing one is indistinguishable from removing a section's styling, and "remove" says that
    // far better. Used for resolving a chosen fingerprint back to its look — the OFFERS come from
    // looksForRole below, which never crosses kinds.
    const allLooks = (plan.data?.templateLooks ?? []).flatMap((role) =>
        role.options.filter((option) => option.hasStyling).map((option) => ({ option, role: role.role })));

    // Like for like. A rich-text band's styling is not a candidate for a product row, whatever else the
    // template has to offer, so a section is only ever shown looks from sections of its own kind — where
    // "product section" is anything that presents products, however they are chosen.
    const looksForRole = (role: string) => allLooks.filter((entry) => entry.role === role);


    const sectionsInPlay = (plan.data?.sectionMatches ?? []).filter((m) => !m.alreadyMatches);

    // The kinds of section actually in play, so bulk actions can be offered per kind rather than globally.
    const rolesInPlay = Array.from(new Set(sectionsInPlay.map((match) => match.role)));

    /**
     * What the apply does if the customer changes nothing — and, in Advanced, the answer each control starts
     * on. Three rules, in order:
     *
     *  - the plan proposed a look for this kind of section: take it (it only proposes when unambiguous);
     *  - the template offers exactly ONE look in total: take that, whatever kind of section it came from, since
     *    it is unambiguously "this template's section look";
     *  - the template styles sections globally and this one has styling of its own: remove it, because that
     *    override is the only thing standing between the template's look and this section.
     *
     * Anything else is left alone: several looks and none for this kind is a genuine choice, and Advanced is
     * where it gets made.
     */
    const defaultAction = (match: typeof sectionsInPlay[number]): SectionAction => {
        if (match.proposedFingerprint) return { kind: 'look', fingerprint: match.proposedFingerprint };
        const own = looksForRole(match.role);
        // One look for this kind of section and no proposal means the plan saw more than one group for the
        // role but only one of them carries styling — still unambiguous once the styling-free one is set aside.
        if (own.length === 1) return { kind: 'look', fingerprint: own[0].option.fingerprint };
        // Nothing for this kind at all: the template styles sections globally as far as this section is
        // concerned, and its own styling is the only thing in the way.
        if (own.length === 0 && match.hasOwnStyling) return { kind: 'clear' };
        return { kind: 'leave' };
    };

    // The default plan as the two state maps hold it. Used to seed on load and to return to when the customer
    // switches back to Recommended.
    const proposedChoices = (): Record<string, string> => {
        const next: Record<string, string> = {};
        for (const match of sectionsInPlay) {
            const action = defaultAction(match);
            if (action.kind === 'look') next[match.sectionId] = action.fingerprint;
        }
        return next;
    };

    const recommendedClears = (): Record<string, boolean> => {
        const next: Record<string, boolean> = {};
        for (const match of sectionsInPlay) next[match.sectionId] = defaultAction(match).kind === 'clear';
        return next;
    };

    const actionOf = (match: typeof sectionsInPlay[number]): SectionAction => {
        if (chosen[match.sectionId]) return { kind: 'look', fingerprint: chosen[match.sectionId] };
        if (styled[match.sectionId]) return { kind: 'style', styleId: styled[match.sectionId] };
        if (toClear[match.sectionId]) return { kind: 'clear' };
        return { kind: 'leave' };
    };

    // Written to both maps at once because the API refuses a section that appears in both lists, and because a
    // control offering three answers must not be able to leave two of them set.
    const setAction = (sectionId: string, action: SectionAction) => {
        setChosen((c) => {
            const next = { ...c };
            if (action.kind === 'look') next[sectionId] = action.fingerprint;
            else delete next[sectionId];
            return next;
        });
        setToClear((c) => ({ ...c, [sectionId]: action.kind === 'clear' }));
        setStyled((s) => {
            const next = { ...s };
            if (action.kind === 'style') next[sectionId] = action.styleId;
            else delete next[sectionId];
            return next;
        });
    };

    const styleName = (styleId: string) => sectionStyleById.get(styleId)?.name ?? 'a section style';

    const actionLabel = (action: SectionAction): string => {
        if (action.kind === 'style') return `Use ${styleName(action.styleId)}`;
        if (action.kind === 'clear') return 'Remove styling';
        if (action.kind === 'leave') return 'Leave as it is';
        const found = allLooks.find((l) => l.option.fingerprint === action.fingerprint);
        return found ? `Use ${lookName(found)}` : 'Use this template\'s look';
    };

    // The source section's own name AND its type. Without the type, "Laptops and mobile workstations" gives no
    // clue whether the look came from a product row or a content band — which is exactly the question a
    // customer has to answer to judge whether it belongs on one of their sections.
    const lookName = (entry: typeof allLooks[number]) =>
        `"${entry.option.sourceTitle || 'Untitled'}" (${sectionTypeLabel(entry.option.sourceType)})`;

    const sectionAfter = (match: typeof sectionsInPlay[number], current: IStorefrontSection) => {
        const action = actionOf(match);
        if (action.kind === 'style') {
            return {
                section: sectionWithStyleRef(current, action.styleId),
                label: `With ${styleName(action.styleId)}`,
            };
        }
        if (action.kind === 'clear') return { section: sectionWithoutStyling(current), label: 'With styling removed' };
        if (action.kind === 'leave') return undefined;
        const look = allLooks.find((l) => l.option.fingerprint === action.fingerprint)?.option;
        return look ? { section: sectionWithLook(current, look), label: 'With this template' } : undefined;
    };

    // What a section's card says has been decided about it. Short by necessity — the cards are a glance across
    // the whole plan, and the detail pane below carries the full wording.
    const shortActionLabel = (action: SectionAction): string => {
        if (action.kind === 'style') return styleName(action.styleId);
        if (action.kind === 'clear') return 'Styling removed';
        if (action.kind === 'leave') return 'Unchanged';
        const found = allLooks.find((l) => l.option.fingerprint === action.fingerprint);
        return found?.option.sourceTitle || "Template's look";
    };

    const renderSectionCard = (match: typeof sectionsInPlay[number]) => {
        const selected = match.sectionId === viewedSectionId;
        const action = actionOf(match);
        const choose = () => setViewedSection(match.sectionId);
        return (
            <div
                key={match.sectionId}
                role='button'
                tabIndex={0}
                aria-pressed={selected}
                onClick={choose}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(); }
                }}
                style={{
                    // The selected card is marked by its border rather than a fill, so a card's own text stays
                    // as readable selected as not.
                    border: `${selected ? 2 : 1}px solid ${selected ? theme.palette.themePrimary : theme.palette.neutralQuaternary}`,
                    borderRadius: 2,
                    // Keeps the row from shifting by a pixel as the border thickens on selection.
                    padding: selected ? '0.5em 0.75em' : 'calc(0.5em + 1px) calc(0.75em + 1px)',
                    minWidth: 150,
                    maxWidth: 220,
                    cursor: 'pointer',
                    background: theme.palette.white,
                    textAlign: 'left',
                }}
            >
                <Text
                    block
                    variant='small'
                    styles={{ root: { fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                >
                    {match.title || 'Untitled section'}
                </Text>
                <Text block variant='xSmall' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    {sectionTypeLabel(match.type)}
                </Text>
                <Text
                    block
                    variant='xSmall'
                    styles={{ root: {
                        color: action.kind === 'leave' ? theme.palette.neutralTertiary : theme.palette.themePrimary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    } }}
                >
                    {shortActionLabel(action)}
                </Text>
            </div>
        );
    };

    const renderSelectedSection = (match: typeof sectionsInPlay[number]) => {
        const current = sectionById[match.sectionId];
        const action = actionOf(match);
        const after = current ? sectionAfter(match, current) : undefined;

        const options: IDropdownOption[] = [
            { key: LEAVE_KEY, text: 'Leave as it is' },
            // Same kind only, so every entry is a look that belongs on this section and none needs a caveat.
            ...looksForRole(match.role).map((entry): IDropdownOption => ({
                key: lookKey(entry.option.fingerprint),
                text: `Use ${lookName(entry)}`,
            })),
            ...(match.hasOwnStyling ? [{ key: CLEAR_KEY, text: 'Remove styling' }] : []),
            // Only when the organization actually keeps section styles. A control that opens onto an empty
            // list teaches the customer the feature is broken rather than that they have not built one yet.
            ...(sectionStyles.length > 0 ? [{ key: STYLE_KEY, text: 'Use a section style…' }] : []),
            // The chosen style itself, so the dropdown can show what was picked rather than falling back to
            // "Leave as it is" and quietly contradicting the preview beside it.
            ...(action.kind === 'style'
                ? [{ key: styleKey(action.styleId), text: `Use ${styleName(action.styleId)}` }]
                : []),
        ];

        const selectedKey = action.kind === 'look' ? lookKey(action.fingerprint)
            : action.kind === 'style' ? styleKey(action.styleId)
            : action.kind === 'clear' ? CLEAR_KEY : LEAVE_KEY;

        return (
            <Stack
                tokens={{ childrenGap: '1em' }}
                styles={{ root: { border: `1px solid ${theme.palette.neutralLight}`, borderRadius: 2, padding: '1em' } }}
            >
                <Stack horizontal horizontalAlign='space-between' verticalAlign='end' wrap tokens={{ childrenGap: 8 }}>
                    <Stack>
                        <Text variant='mediumPlus'>{match.title || 'Untitled section'}</Text>
                        <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                            {sectionTypeLabel(match.type)}
                        </Text>
                    </Stack>
                    <Dropdown
                        selectedKey={selectedKey}
                        options={options}
                        styles={{ root: { minWidth: 260 } }}
                        onChange={(_e, item) => {
                            if (!item) return;
                            const key = String(item.key);
                            if (key === LEAVE_KEY) setAction(match.sectionId, { kind: 'leave' });
                            else if (key === CLEAR_KEY) setAction(match.sectionId, { kind: 'clear' });
                            else if (key === STYLE_KEY) setStylePickerFor(match.sectionId);
                            // Re-selecting the style already chosen: the picker reopens, so the choice can be
                            // changed from the same control it was made with.
                            else if (key.startsWith('style:')) setStylePickerFor(match.sectionId);
                            else setAction(match.sectionId, { kind: 'look', fingerprint: key.slice('look:'.length) });
                        }}
                    />
                </Stack>

                {current && (
                    <Stack horizontal wrap tokens={{ childrenGap: 8 }} verticalAlign='stretch'>
                        {/* Equal halves: flexBasis 0 with grow ignores the content's own width, and the
                            explicit minWidth overrides a flex item's default min-width:auto — which would
                            otherwise be the preview's 1000px design width and push both columns off the panel.
                            `wrap` plus that minimum is what stacks the pair on a narrow screen. */}
                        <Stack.Item grow styles={{ root: { flexBasis: 0, minWidth: MIN_PREVIEW_COLUMN } }}>
                            <SectionRealPreview
                                label='Now'
                                section={current}
                                storefrontId={props.storefrontId}
                                muted={!!after}
                            />
                        </Stack.Item>
                        <Stack.Item grow styles={{ root: { flexBasis: 0, minWidth: MIN_PREVIEW_COLUMN } }}>
                            {after && (
                                <SectionRealPreview
                                    label={after.label}
                                    section={after.section}
                                    storefrontId={props.storefrontId}
                                    note={action.kind === 'clear'
                                        ? "The template's colors, cards and section backgrounds reach this "
                                            + 'section once its own styling is gone.'
                                        : undefined}
                                />
                            )}
                        </Stack.Item>
                    </Stack>
                )}
            </Stack>
        );
    };

    // Scoped to one kind of section: applying a look to "every section" would mean putting a product row's
    // styling on a content band, which is the thing this flow does not do.
    // What the style picker is deciding about, and how many other sections would come along with it.
    const stylePickerMatch = sectionsInPlay.find((m) => m.sectionId === stylePickerFor);
    const stylePickerLikeCount = stylePickerMatch
        ? sectionsInPlay.filter((m) => m.role === stylePickerMatch.role && m.sectionId !== stylePickerFor).length
        : 0;

    const applyStyleChoice = (styleId: string, applyToLike: boolean) => {
        if (!stylePickerFor) return;
        // "Like" means the same kind, exactly as everywhere else in this step: a style chosen for a product
        // row goes to the other product rows and nowhere near a content block.
        const targets = applyToLike && stylePickerMatch
            ? sectionsInPlay.filter((m) => m.role === stylePickerMatch.role).map((m) => m.sectionId)
            : [stylePickerFor];
        for (const sectionId of targets) setAction(sectionId, { kind: 'style', styleId });
        setStylePickerFor(undefined);
    };

    const applyToRole = (role: string, action: SectionAction) => {
        for (const match of sectionsInPlay) {
            if (match.role !== role) continue;
            // Never offer to remove styling from a section that has none: the answer would be a no-op row.
            if (action.kind === 'clear' && !match.hasOwnStyling) continue;
            setAction(match.sectionId, action);
        }
    };

    // Sections the default plan has no answer for: the template offers looks, but none for this kind of
    // section, so choosing one is a real decision rather than something to default. While any exist there is
    // nothing honest to put in a Recommended summary, so the step only offers Advanced.
    const undecidable = sectionsInPlay.filter((match) =>
        defaultAction(match).kind === 'leave' && (looksForRole(match.role).length > 0 || match.hasOwnStyling));
    const sectionsNeedChoosing = undecidable.length > 0;
    const effectiveSectionDetail = sectionsNeedChoosing ? 'advanced' : sectionDetail;

    // Falls back to the first section rather than nothing: an empty detail pane below a row of cards reads as
    // broken, and there is always a sensible one to start on.
    const viewedMatch = sectionsInPlay.find((m) => m.sectionId === viewedSection) ?? sectionsInPlay[0];
    const viewedSectionId = viewedMatch?.sectionId;

    const renderRecommendedSections = () => (
        <Stack tokens={{ childrenGap: '0.75em' }}>
            <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                A section keeps its own title, content, images and products either way — only its styling
                changes. Switch to Advanced to choose per section.
            </Text>
            <Stack tokens={{ childrenGap: 4 }}>
                {sectionsInPlay.map((match) => (
                    <Stack key={match.sectionId} horizontal wrap tokens={{ childrenGap: 6 }} verticalAlign='center'>
                        <Text variant='small' styles={{ root: { fontWeight: 600 } }}>
                            {match.title || 'Untitled section'}
                        </Text>
                        <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                            {actionLabel(actionOf(match)).toLowerCase()}
                        </Text>
                    </Stack>
                ))}
            </Stack>
            {alreadyMatching > 0 && (
                <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    {alreadyMatching} section{alreadyMatching === 1 ? '' : 's'} already
                    {alreadyMatching === 1 ? ' matches' : ' match'} this template.
                </Text>
            )}
            {addable.length > 0 && (
                <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    This template also has {addable.length} section{addable.length === 1 ? '' : 's'} of its own
                    you could add as a starting point. Advanced is where to pick
                    {addable.length === 1 ? ' it' : ' them'}.
                </Text>
            )}
        </Stack>
    );

    const renderAdvancedSections = () => (
        <Stack tokens={{ childrenGap: '1em' }}>
            <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                A section keeps its own title, content, images and products whatever you choose — only its
                styling changes.
            </Text>

            {rolesInPlay.map((role) => {
                const looks = looksForRole(role);
                const anyOwnStyling = sectionsInPlay.some((m) => m.role === role && m.hasOwnStyling);
                if (looks.length === 0 && !anyOwnStyling) return null;
                const count = sectionsInPlay.filter((m) => m.role === role).length;
                const roleLabel = ROLE_LABELS[role] ?? role;
                return (
                    <Stack key={role} horizontal wrap tokens={{ childrenGap: 8 }} verticalAlign='center'>
                        <Text variant='small'>
                            Apply to all {count} {roleLabel}:
                        </Text>
                        {looks.map((entry) => (
                            <DefaultButton
                                key={entry.option.fingerprint}
                                text={looks.length === 1 ? 'This template\'s look' : lookName(entry)}
                                iconProps={{ iconName: 'Color' }}
                                styles={{ icon: { color: BUTTON_ICON_GREEN } }}
                                onClick={() => applyToRole(role, { kind: 'look', fingerprint: entry.option.fingerprint })}
                            />
                        ))}
                        {anyOwnStyling && (
                            <DefaultButton
                                text='Remove styling'
                                iconProps={{ iconName: 'Delete' }}
                                styles={{ icon: { color: BUTTON_ICON_GREEN } }}
                                onClick={() => applyToRole(role, { kind: 'clear' })}
                            />
                        )}
                        <DefaultButton
                            text='Leave alone'
                            iconProps={{ iconName: 'Uneditable' }}
                            styles={{ icon: { color: BUTTON_ICON_GREEN } }}
                            onClick={() => applyToRole(role, { kind: 'leave' })}
                        />
                    </Stack>
                );
            })}

            {alreadyMatching > 0 && (
                <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    {alreadyMatching} section{alreadyMatching === 1 ? '' : 's'} already
                    {alreadyMatching === 1 ? ' matches' : ' match'} this template and
                    {alreadyMatching === 1 ? ' is' : ' are'} not listed.
                </Text>
            )}

            {/* A glance across the whole plan — every section, with what has been decided about it — and the
                selector for which one to look at closely. Horizontally scrollable rather than wrapped, so the
                row stays one line and the detail below it does not move as sections are picked. */}
            <Stack
                horizontal
                tokens={{ childrenGap: 8 }}
                // Extra room above, on top of the step's own gap: the bulk controls act on everything at once
                // and the cards are the per-section list, so they read as one undifferentiated block when
                // spaced the same as everything else.
                //
                // PADDING, not margin. A Stack's childrenGap is implemented as a `> *:not(:first-child)`
                // margin-top rule on the parent, and that selector outranks a plain class on the child - so a
                // marginTop here was simply overridden and did nothing.
                styles={{ root: { overflowX: 'auto', paddingBottom: 4, paddingTop: '1em' } }}
            >
                {sectionsInPlay.map(renderSectionCard)}
            </Stack>

            {viewedMatch && renderSelectedSection(viewedMatch)}

            {addable.length > 0 && <Separator />}
            {renderAddSections()}
        </Stack>
    );

    const renderAddSections = () => {
        if (addable.length === 0) return null;
        return (
            <Stack tokens={{ childrenGap: '0.5em' }}>
                <Text variant='mediumPlus'>Add a section from this template</Text>
                <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    A designed starting point rather than a blank one. Each is added to the end of the page and
                    switched OFF, so nothing shoppers see changes until you enable it, and none of them carries
                    the template&apos;s products.
                </Text>
                {addable.map((section) => (
                    <Checkbox
                        key={section.sectionId}
                        checked={!!toAdd[section.sectionId]}
                        onChange={(_e, checked) => setToAdd((a) => ({ ...a, [section.sectionId]: !!checked }))}
                        onRenderLabel={() => (
                            <Stack horizontal wrap tokens={{ childrenGap: 6 }} verticalAlign='center'>
                                <Text variant='small' styles={{ root: { fontWeight: 600 } }}>
                                    {section.title || 'Untitled section'}
                                </Text>
                                <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                                    {sectionTypeLabel(section.type)}
                                    {!section.hasStyling && ' — takes its look from the theme'}
                                </Text>
                            </Stack>
                        )}
                    />
                ))}
            </Stack>
        );
    };

    const renderSectionsStep = () => {
        if (!sectionsAvailable) {
            return (
                <MessageBar messageBarType={MessageBarType.info}>
                    This storefront has no draft yet. Apply the look first — that creates one — then reopen this
                    to restyle sections.
                </MessageBar>
            );
        }
        if (sectionsInPlay.length === 0 && addable.length === 0) {
            return (
                <Stack tokens={{ childrenGap: '0.5em' }}>
                    <Text>There are no sections to change.</Text>
                    {alreadyMatching > 0 && (
                        <Text variant='small'>
                            {alreadyMatching} already {alreadyMatching === 1 ? 'matches' : 'match'} this
                            template&apos;s styling.
                        </Text>
                    )}
                </Stack>
            );
        }

        return (
            <Stack tokens={{ childrenGap: '1em' }}>
                {templateStylesSectionsGlobally && (
                    <Text variant='small'>
                        This template does not style sections individually — its section appearance comes from
                        the theme, the section background colors and the home page styling. Removing a
                        section&apos;s own styling is what lets that through.
                    </Text>
                )}

                {sectionsNeedChoosing ? (
                    <MessageBar messageBarType={MessageBarType.warning}>
                        {undecidable.length === 1 ? 'One section needs' : `${undecidable.length} sections need`}
                        {' '}a decision: this template has no look for
                        {undecidable.length === 1 ? ' its kind of section' : ' their kinds of section'}, so there
                        is nothing to recommend. Choose below, or leave
                        {undecidable.length === 1 ? ' it' : ' them'} as
                        {undecidable.length === 1 ? ' it is' : ' they are'}.
                    </MessageBar>
                ) : (
                    <Pivot
                        selectedKey={sectionDetail}
                        onLinkClick={(item) => (item?.props.itemKey === 'advanced'
                            ? setSectionDetail('advanced')
                            : chooseRecommendedSections())}
                    >
                        <PivotItem itemKey='recommended' headerText='Recommended' />
                        <PivotItem itemKey='advanced' headerText='Advanced' />
                    </Pivot>
                )}

                {effectiveSectionDetail === 'recommended' ? renderRecommendedSections() : renderAdvancedSections()}
            </Stack>
        );
    };

    const renderReviewStep = () => (
        <Stack tokens={{ childrenGap: '0.75em' }}>
            <Text variant='mediumPlus'>Ready to apply {plan.data?.templateName ?? props.templateName}</Text>
            <Stack tokens={{ childrenGap: 2 }}>
                <Text variant='small'>
                    {applyingCount(changesByGroup.look)} styling
                    {' '}{applyingCount(changesByGroup.look) === 1 ? 'setting' : 'settings'}
                    {optedInChangeCount > 0 && `, plus ${optedInChangeCount} you opted into`}
                    {exclusions.length > 0 && `, with ${exclusions.length} left as ${exclusions.length === 1 ? 'it is' : 'they are'}`}
                </Text>
                {mergedChangeCount > 0 && (
                    <Text variant='small'>
                        {mergedChangeCount} merged {mergedChangeCount === 1 ? 'page' : 'pages'} of styling,
                        keeping your links, store name, shortcut tiles and copy
                    </Text>
                )}
                {homeNavigation !== 'keepCurrent' && (
                    <Text variant='small'>
                        {homeNavigation === 'useTemplateNavPods'
                            ? "Home navigation: the template's shortcut tiles, category tiles off"
                            : 'Home navigation: both the category tiles and the template\'s shortcut tiles'}
                    </Text>
                )}
                <Text variant='small'>
                    {chosenSections.length} section{chosenSections.length === 1 ? '' : 's'} restyled
                </Text>
                {addedSections.length > 0 && (
                    <Text variant='small'>
                        {addedSections.length} section{addedSections.length === 1 ? '' : 's'} added from the
                        template, switched off
                    </Text>
                )}
                {clearedSections.length > 0 && (
                    <Text variant='small'>
                        {clearedSections.length} section{clearedSections.length === 1 ? '' : 's'} with
                        styling removed
                    </Text>
                )}
            </Stack>
            <MessageBar messageBarType={MessageBarType.info}>
                This saves to your draft, so shoppers keep seeing the storefront as it is now. Preview the
                draft, then publish to make it live, or revert to discard it.
            </MessageBar>
            {nothingToDo && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    Nothing is selected, so applying would change nothing.
                </MessageBar>
            )}
        </Stack>
    );

    const steps: Step[] = ['look', 'sections', 'review'];
    const stepIndex = steps.indexOf(step);

    const renderFooter = () => (
        <Stack horizontal tokens={{ childrenGap: '0.5em' }}>
            {stepIndex > 0 && (
                <DefaultButton
                    text='Back'
                    iconProps={{ iconName: 'ChevronLeft' }}
                    onClick={() => setStep(steps[stepIndex - 1])}
                    disabled={applyState.isLoading}
                />
            )}
            {step !== 'review' && (
                <PrimaryButton
                    text='Next'
                    // menuIconProps, not iconProps, so the chevron TRAILS the label — it points the way
                    // forward, and an arrow ahead of the word points back at it. Fluent renders this slot
                    // without a menu and only sets aria-haspopup for a real menuProps, so it stays a
                    // plain button to a screen reader. It does, however, size the trailing slot at
                    // `small` and the leading one at `mediumPlus`, so Back's chevron comes out visibly
                    // bigger than Next's until this puts them on the same size.
                    menuIconProps={{ iconName: 'ChevronRight' }}
                    styles={{ menuIcon: { fontSize: theme.fonts.mediumPlus.fontSize } }}
                    onClick={() => setStep(steps[stepIndex + 1])}
                    disabled={plan.isLoading}
                />
            )}
            {step === 'review' && (
                <PrimaryButton
                    text={applyState.isLoading ? 'Applying...' : 'Apply to draft'}
                    onClick={apply}
                    disabled={applyState.isLoading || nothingToDo}
                    onRenderIcon={applyState.isLoading ? () => <Spinner size={SpinnerSize.xSmall} /> : undefined}
                />
            )}
            <DefaultButton
                text='Cancel'
                iconProps={{ iconName: 'Cancel' }}
                onClick={props.onDismiss}
                disabled={applyState.isLoading}
            />
        </Stack>
    );

    return (
        // StandardPanel rather than a bare Fluent Panel: it paints the footer SLOT white with a top border, so
        // the pinned buttons do not float over the scrolling change list. Fluent leaves that region
        // transparent, and the step's content is long enough to always be scrolling behind it.
        <StandardPanel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            type={PanelType.large}
            headerText={`Apply ${plan.data?.templateName ?? props.templateName ?? 'template'}`}
            onRenderFooterContent={renderFooter}
            isFooterAtBottom
        >
            <Stack tokens={{ childrenGap: '1em' }}>
                <Text variant='small' styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    Step {stepIndex + 1} of {steps.length}
                    {step === 'look' && ' — what changes'}
                    {step === 'sections' && ' — sections'}
                    {step === 'review' && ' — review'}
                </Text>

                {plan.isLoading && <Spinner label='Working out what would change...' />}
                {plan.isError && (
                    <MessageBar messageBarType={MessageBarType.error}>
                        This template&apos;s changes could not be worked out. Applying a template needs
                        storefront previews enabled.
                    </MessageBar>
                )}
                {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}

                {plan.data && step === 'look' && renderLookStep()}
                {plan.data && step === 'sections' && (
                    <StorefrontStylesProvider
                        styles={storefrontStyles.data?.items ?? []}
                        storefrontId={props.storefrontId}
                        canEdit={false}
                    >
                        {renderSectionsStep()}
                        {/* Inside the provider deliberately: the whole point of the preview is to resolve the
                            $styleRef it is about to write, and outside it the modal would show the section
                            with no styling at all and call it the result. */}
                        <SectionStylePickerModal
                            isOpen={!!stylePickerFor}
                            section={stylePickerFor ? sectionById[stylePickerFor] : undefined}
                            storefrontId={props.storefrontId}
                            styles={sectionStyles}
                            currentStyleId={stylePickerFor ? styled[stylePickerFor] : undefined}
                            likeCount={stylePickerLikeCount}
                            likeLabel={stylePickerMatch ? ROLE_LABELS[stylePickerMatch.role] ?? 'sections' : 'sections'}
                            onApply={applyStyleChoice}
                            onDismiss={() => setStylePickerFor(undefined)}
                        />
                    </StorefrontStylesProvider>
                )}
                {plan.data && step === 'review' && renderReviewStep()}
            </Stack>
        </StandardPanel>
    );
};

export default ApplyTemplateWizard;
